import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import { isMobile } from 'react-device-detect';

import Utils from '../utils';
import ShipStationCommunication from './ShipStationCommunication';
import ShipStationState from './ShipStationState.enum';

const { Session, Logger, Navigation } = Utils;

export default class ShipStation {
	static lookup = {};

	/**
	 * Getter.
	 * Return `true` if the following conditions are met:
	 * - CanSeeShipStationUpgrade Registry key is true
	 * - Plan Id and Pending Plan Id should not be included in SisterCompanyPlanIds
	 * - Not an enterprise user (corporationId is 0)
	 * - Not a SSO redirection
	 * - Not a Responsive Device
	 *
	 * @return {boolean}
	 */
	static get canSeeUpgrade() {
		const { calculatedSettings, accountInfo, settings } = Session.getData();
		const { isSso } = calculatedSettings;
		const { corporationId, planDetails = {} } = accountInfo;

		// check registry value if user could see upgrade option
		const canSeeShipStationUpgrade = settings.CanSeeShipStationUpgrade === true;

		// check if user is from any sister company plan
		const planId = isNumber(planDetails.planId)
			? planDetails.planId.toString()
			: '-1';
		const pendingPlanId = isNumber(planDetails.pendingPlanId)
			? planDetails.pendingPlanId.toString()
			: '-1';
		const sisterCompanyPlanIdsArray = isString(settings.sisterCompanyPlanIds)
			? settings.sisterCompanyPlanIds.split(',')
			: [];
		const isFromSisterCompanyPlan =
			sisterCompanyPlanIdsArray.indexOf(planId) !== -1 ||
			sisterCompanyPlanIdsArray.indexOf(pendingPlanId) !== -1;

		// check if user is an enterprise user
		const isEnterpriseUser = corporationId !== 0;

		return (
			canSeeShipStationUpgrade === true &&
			isFromSisterCompanyPlan === false &&
			isEnterpriseUser === false &&
			isSso === false &&
			isMobile === false
		);
	}

	/**
	 * Return <ShipStationState> enum value.
	 * @return {ShipStationState}
	 */
	static getShipStationState() {
		const settings = Session.getSettings();

		if (settings.ShipStationState === ShipStationState.Connected) {
			return ShipStationState.Connected;
		} else if (settings.ShipStationState === ShipStationState.NotConnected) {
			return ShipStationState.NotConnected;
		}

		return ShipStationState.Error;
	}

	/**
	 * Make a HTTP request to fetch user's lookup object when user has registered in ShipStation.
	 * @param {boolean} forceReload `true` to force to reload the lookup object.
	 * @return {object}
	 */
	static async getLookup(forceReload) {
		// check if user has registered in ShipStation
		if (this.isShipStationConnected()) {
			// fetch the data when local lookup object is empty or when `forceReload` flag is `true`
			if (isEmpty(this.lookup) || forceReload === true) {
				const response = await ShipStationCommunication.loadLookup();

				if (ShipStationCommunication.isSuccessful(response)) {
					const { data = {} } = response;
					const lookupData = isObject(data.data) ? data.data : {};

					// store lookup data in this class
					this.lookup = lookupData;
				}
			}
		}

		// return the object to the caller
		return this.lookup;
	}

	/**
	 * Return `true` if user has registered in ShipStation.
	 * @return {boolean}
	 */
	static isShipStationConnected() {
		return this.getShipStationState() === ShipStationState.Connected;
	}

	/**
	 * Make a HTTP call to store settings in ShipStation.
	 * @param {string} settings The string format is "key1=<val1>&key2=<val2>&...".
	 */
	static async saveSettings(settings) {
		// check if user has registered in ShipStation
		if (this.isShipStationConnected()) {
			const response = await ShipStationCommunication.saveSettings(settings);

			if (ShipStationCommunication.isSuccessful(response)) {
				const { data = {} } = response;

				// new settings stored in ShipStation
				const newSettingsData = isObject(data.settings) ? data.settings : {};

				// current settings stored in local lookup object
				const oldSettingsData = isObject(this.lookup.Settings)
					? this.lookup.Settings
					: {};

				// merge the new Settings in lookup object
				this.lookup.Settings = {
					...oldSettingsData,
					...newSettingsData,
				};
			}

			return response;
		}
	}

	/**
	 * Return Settings object in local lookup object.
	 * @return {object}
	 */
	static getSettings() {
		return isObject(this.lookup.Settings) ? { ...this.lookup.Settings } : {};
	}

	// Redirect to ShipStation sign-in page.
	static goToShipStationSignIn() {
		const { ShipStationSignInUrl } = Session.getSettings();

		Navigation.redirect(`${ShipStationSignInUrl}`);
	}

	// Redirect to Orders page and show marketing modal.
	static async goToRegistration() {
		const ordersUrl = await Navigation.getNavigationUrl('orders');

		if (ordersUrl) {
			Navigation.redirect(`${ordersUrl}&marketing-view`);
		}
	}

	// Redirect to Orders page and show "Add Advanced Shipping Features!" modal.
	static async goToUpgrade() {
		const ordersUrl = await Navigation.getNavigationUrl('orders');

		if (ordersUrl) {
			Navigation.redirect(`${ordersUrl}&showinfo=true`);
		}
	}

	/**
	 * Make a HTTP request to register user in ShipStation.
	 * @param {string} stampsUsername
	 * @param {string} stampsPassword
	 * @param {function} callback
	 */
	static async upgradeShipStation(stampsUsername, stampsPassword, callback) {
		const { customerDetails = {}, customerId } = Session.getAccountInfo();
		const { PrintMedia: PrintOn } = Session.getPreferences();
		const { address = {}, email } = customerDetails;
		const nameParts = isString(address.name) ? address.name.split(' ') : [];
		const firstName = nameParts[0] || '';
		const lastName =
			nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

		// make a call to register user in ShipStation
		const response = await ShipStationCommunication.upgradeWebbatchAccount({
			RequestedUsername: stampsUsername,
			StampsUsername: stampsUsername,
			StampsPassword: stampsPassword,
			FirstName: firstName,
			LastName: lastName,
			CompanyName:
				address.companyName !== '' ? address.companyName : `${address.name}`,
			StampsWebbatchEmail: `orders_${customerId}@stamps.com`,
			RequestedEmail: email,
			PhoneNumber: address.phone,
			PrintOn,

			// TODO: the following is commented out because React projects are hosted in different
			// domains, and cookie across domains won't work. (But, there's a way!)

			// Note: 'upgradeTrigger' cookie is set in following places (ExtJ Projects):
			// 1. Marketing Campaign. See MarketingCampaignManager.js
			// 2. Marketplace stores. See StoreSelectionWindowController.js.
			// 3. Upgrade link in ContentService. See Header.js
			// UpgradeTrigger: Util.getCookie('upgradeTrigger'),
		});

		if (ShipStationCommunication.isSuccessful(response)) {
			// TODO: Session.SavePreferences is not available in dev/React branch.
			// Revisit here when it's merged down in dev/ReactSetting branch.
			// set the user preference
			// Session.SavePreferences({
			// 	Name: 'HasUpgradedToShipStation',
			// 	Value: '1',
			// });

			// log successful connection
			Logger.logToServer({
				category: 'info_successfulUpgrade',
				usernameModified: response.UsernameModified,
			});

			// redirect to shipstation
			return Navigation.redirect(`${response.LoginUrl}`);
		}

		// log the error
		// Logger.logToServer(
		// 	{
		// 		category: 'error_details',
		// 		errorType: response.response.Property,
		// 		errorMessage: response.response.ErrorMessage,
		// 		errorString: response.status,
		// 	},
		// 	true
		// );

		if (callback) {
			callback(response);
		}
	}

	/**
	 * Make a HTTP request to authenticate user.
	 * @param {string} userName
	 * @param {string} password
	 * @param {function} callback Callback if authentication or upgradeWebbatchAccount call fails.
	 */
	static async authenticateAndUpgrade(userName, password, callback) {
		const response = await ShipStationCommunication.authenticateUser({
			Username: userName,
			Password: password,
			loginSource: 'ShipStationLogin',
		});

		if (ShipStationCommunication.isSuccessful(response)) {
			this.upgradeShipStation(userName, password, callback);
		} else if (callback) {
			callback(response);
		}
	}
}
